import { apiErrorMessage, useCreateCompanyClass, useMergeCompanies } from "@vidario/api-client";
import {
  CANDIDATE_NOUN,
  classNameError,
  countLabel,
  employerRef,
  type CompanyClassSummary,
  type EmployerGroup,
} from "@vidario/domain";
import { Button, Input, Modal, useToast } from "@vidario/ui";
import { useState, type ReactElement } from "react";

interface MergeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rows: readonly EmployerGroup[];
  classes: readonly CompanyClassSummary[];
  onMerged: (text: string) => void;
}

/** „Zusammenfassen": several spelling groups become one company. The admin
 * picks the name to show; that row's class is kept. */
export function MergeDialog({
  open,
  onOpenChange,
  rows,
  classes,
  onMerged,
}: MergeDialogProps): ReactElement {
  // Same rule as the main spelling: most candidates, then the shorter name.
  const mostCandidates = [...rows].sort(
    (a, b) => b.candidateCount - a.candidateCount || a.name.length - b.name.length,
  )[0];
  const [mainId, setMainId] = useState<string | undefined>(mostCandidates?.id);
  const merge = useMergeCompanies();
  const { push } = useToast();

  const main = rows.find((r) => r.id === mainId) ?? mostCandidates;
  const classIds = new Set(rows.map((r) => r.companyClassId));
  const mainClass = classes.find((c) => c.id === main?.companyClassId);

  const submit = (): void => {
    if (!main) return;
    merge.mutate(
      { items: rows.map(employerRef), main: employerRef(main) },
      {
        onSuccess: () => {
          const text = `${rows.length} Firmen zu „${main.name}“ zusammengefasst`;
          push({ kind: "success", message: text });
          onMerged(text);
          onOpenChange(false);
        },
        onError: (err) =>
          push({
            kind: "error",
            title: "Zusammenfassen fehlgeschlagen",
            message: apiErrorMessage(err),
          }),
      },
    );
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title={`${rows.length} Firmen zusammenfassen`}
      description="Diese Schreibweisen gehören zur selben Firma. Welcher Name soll angezeigt werden?"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button onClick={submit} loading={merge.isPending}>
            Zusammenfassen
          </Button>
        </>
      }
    >
      <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
        <legend className="sr-only">Angezeigter Name</legend>
        {rows.map((row) => (
          <label key={row.id} className="flex cursor-pointer items-center gap-3 text-sm">
            <input
              type="radio"
              name="merge-main"
              value={row.id}
              checked={row.id === main?.id}
              onChange={() => setMainId(row.id)}
              className="accent-primary-600"
            />
            <span className="flex-1">{row.name}</span>
            <span className="tabular-nums text-text-tertiary">
              {countLabel(row.candidateCount, CANDIDATE_NOUN)}
            </span>
          </label>
        ))}
      </fieldset>
      {classIds.size > 1 && main && (
        <p className="mt-4 mb-0 text-sm text-text-secondary">
          Die Klasse von „{main.name}“ wird übernommen{mainClass ? ` („${mainClass.name}“)` : ""}.
        </p>
      )}
    </Modal>
  );
}

interface NewClassDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  classes: readonly CompanyClassSummary[];
  onCreated: (created: CompanyClassSummary) => void;
}

export function NewClassDialog({
  open,
  onOpenChange,
  classes,
  onCreated,
}: NewClassDialogProps): ReactElement {
  const [name, setName] = useState("");
  const [touched, setTouched] = useState(false);
  const create = useCreateCompanyClass();
  const { push } = useToast();
  const error = touched ? classNameError(name, classes) : null;

  const close = (next: boolean): void => {
    if (!next) {
      setName("");
      setTouched(false);
    }
    onOpenChange(next);
  };

  const submit = (): void => {
    setTouched(true);
    if (classNameError(name, classes)) return;
    create.mutate(name, {
      onSuccess: (created) => {
        push({ kind: "success", message: `Klasse „${created.name}“ angelegt` });
        onCreated(created);
        close(false);
      },
      onError: (err) =>
        push({ kind: "error", title: "Klasse nicht angelegt", message: apiErrorMessage(err) }),
    });
  };

  return (
    <Modal
      open={open}
      onOpenChange={close}
      size="sm"
      title="Neue Klasse"
      footer={
        <>
          <Button variant="secondary" onClick={() => close(false)}>
            Abbrechen
          </Button>
          <Button type="submit" form="new-company-class" loading={create.isPending}>
            Anlegen
          </Button>
        </>
      }
    >
      <form
        id="new-company-class"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Input
          label="Name der Klasse"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => setTouched(true)}
          error={error}
        />
      </form>
    </Modal>
  );
}
